import styled from '@emotion/styled';
import { IconArrowLeft } from "@kompare/kompare-ui-library";
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import aircash from 'src/assets/images/Aircash.png';
import keks from 'src/assets/images/keks.png';
import {
  formatNumber,
  i18n,
  openCallMeModal,
  ProductTypeCodeEnum,
  setOfferId,
  setProgressBarPercentage,
  useAppDispatch
} from 'src/common';
import { useGetCheckoutDataAdditionalQuery } from 'src/common/api/apollo/additional';
import {
  HealthOfferPaymentOptionEnum,
  Accordion,
  ArrowRightIcon,
  Button,
  Card,
  CardIcon,
  Flex,
  Installments,
  Loader,
  RequestAgentCardV2,
  SlipIcon,
  Text,
  WsPayIframe
} from 'src/components';
import { GeneralTranferPaymentInfoCardAdditional } from 'src/components/cards/additional';
import config from 'src/config';
import { colors } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';

const LogoStyle = styled.img({
  height: '30px',
});

const ButtonContainerStyle = styled(Flex)({
  marginTop: 20,
  marginBottom: 20,
});

const CardStyle = styled(Card)({
  padding: '8px 16px',
  textAlign: 'center',
});

const TextStyle = styled(Text)({
  textAlign: 'center',
});


const CheckoutPageAdditional = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  const offerId = params.offerId as string;

  const [openPaymentMethodIndex, setOpenPaymentMethodIndex] = useState<number | null>(null);
  const [iframeLoadingStates, setIframeLoadingStates] = useState<{ [key: string]: boolean }>({});

  const { data: checkoutData, loading: checkoutDataLoading } = useGetCheckoutDataAdditionalQuery({
    variables: { id: offerId },
    skip: !offerId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (!data.getOffer?.productTypeSpecific?.healthQuestionnaire?.isConfirmed) {
        navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_NEEDS_CONFIRMATION.fullPath}/${params.offerId}`);
      }
      if (data.getOffer?.productTypeSpecific?.healthQuestionnaire?.rejected) {
        navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_REJECTED.fullPath}/${params.offerId}`);
      }
    }
  });

  useEffect(() => {
    dispatch(setProgressBarPercentage(100));
  }, []);

  useEffect(() => {
    const twelveAndHalfMinutesMs = (12 * 60 + 30) * 1000;
    const timeoutId = window.setTimeout(() => {
      window.location.reload();
    }, twelveAndHalfMinutesMs);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const success = queryParams.get('Success');
    if (success) {
      navigate(`${AppRoutesAdditional.CHECKOUT_FAILURE.fullPath}?ErrorCodes=${success}`);
    }
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  useEffect(() => {
    // 1) Označi trenutni zapis kao "checkout" bez stvaranja novog history entry-a
    try {
      window.history.replaceState(
        { ...(window.history.state || {}), __isCheckout: true },
        document.title,
        window.location.href
      );
    } catch (e) {
      console.error('Error replacing state:', e);
    }

    // 2) Prereži sve "forward" unose dodavanjem current checkouta na kraj
    try {
      window.history.pushState(
        { ...(window.history.state || {}), __isCheckout: true },
        document.title,
        window.location.href
      );
    } catch (e) {
      console.error('Error pushing state:', e);
    }

    // 3) Ako korisnik stisne Back i dođe na stariji "checkout" zapis, preskoči ga do prave prethodne stranice
    const onPopState = () => {
      if (window.history.state && window.history.state.__isCheckout) {
        window.history.go(-1);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleIframeLoad = (iframeKey: string) => {
    setIframeLoadingStates(prev => ({
      ...prev,
      [iframeKey]: false
    }));
  };

  const paymentOptions = useMemo(() => {
    if (!checkoutData?.getOfferPaymentOptions?.paymentOptions) return [];
    return [
      {
        key: 'creditCard',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.onetimeCard'),
        icon: <CardIcon color={colors.icon1} width={24} height={24} />,
        lazy: false,
        content: (
          <WsPayIframe type={ProductTypeCodeEnum.DODATNO} key='creditCard' offerId={offerId!} paymentOption={HealthOfferPaymentOptionEnum.CreditCard} paymentType="visa" onLoad={() => handleIframeLoad('creditCard')} />
        ),
      },
      {
        key: 'creditCardWithInstallments',
        type: 'installments',
        title: i18n.t('pages.checkoutPage.installmentsCard'),
        icon: <CardIcon color={colors.icon1} width={24} height={24} />,
        lazy: false,
        content: (
          <Flex vertical gap={24}>
            <Flex gap={20}>
              <ArrowRightIcon color={colors.icon1} />
              <Installments data={checkoutData?.getOfferPaymentOptions}></Installments>
            </Flex>
            <CardStyle radius={8} background={colors.primary5}>
              <Text type="small">{i18n.t('pages.checkoutPage.installmentsCardDescription')}</Text>
            </CardStyle>
            <WsPayIframe
              type={ProductTypeCodeEnum.DODATNO}
              key="creditCardWithInstallments"
              offerId={offerId!}
              paymentOption={HealthOfferPaymentOptionEnum.CreditCard}
              paymentType="visa"
              installments
              onLoad={() => handleIframeLoad('creditCardWithInstallments')}
            />
          </Flex>
        ),
      },
      {
        key: 'generalTransfer',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.onetimeAccount'),
        icon: <SlipIcon color={colors.icon1} width={24} height={24} />,
        lazy: true,
        content: <GeneralTranferPaymentInfoCardAdditional type="oneTime" offerId={offerId} />,
      },
      {
        key: 'generalTransferWithInstallments',
        type: 'installments',
        title: i18n.t('pages.checkoutPage.installmentsAccount'),
        icon: <SlipIcon color={colors.icon1} width={24} height={24} />,
        lazy: true,
        content: <GeneralTranferPaymentInfoCardAdditional type="installments" offerId={offerId} />,
      },
      {
        key: 'keksPay',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.keksPay'),
        icon: <LogoStyle src={keks} alt="" />,
        lazy: true,
        content:
          config.REACT_APP_IS_DEV === 'true' ? (
            'content'
          ) : (
            <WsPayIframe
              key="keksPay"
              type={ProductTypeCodeEnum.DODATNO}
              offerId={offerId}
              paymentOption={HealthOfferPaymentOptionEnum.KeksPay}
              paymentType="keksPay"
              onLoad={() => handleIframeLoad('keksPay')}
            />
          ),
      },
      {
        key: 'aircash',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.aircash'),
        icon: <LogoStyle src={aircash} alt="" />,
        lazy: true,
        content:
          config.REACT_APP_IS_DEV === 'true' ? (
            'content'
          ) : (
            <WsPayIframe
              key="aircash"
              type={ProductTypeCodeEnum.DODATNO}
              offerId={offerId}
              paymentOption={HealthOfferPaymentOptionEnum.Aircash}
              paymentType="aircash"
              onLoad={() => handleIframeLoad('aircash')}
            />
          ),
      },
    ]
      .filter((pm) => {
        // Prvo provjerimo postoji li opcija plaćanja u paymentOptions
        const hasPaymentOption =
          (checkoutData?.getOfferPaymentOptions?.paymentOptions?.find((po: any) => po.paymentOption === pm.key)
            ?.paymentTypes?.length ?? 0) > 0;


        return hasPaymentOption;
      })
      .map((pm) => {
        // Pronađi odgovarajuću payment opciju iz podataka
        const paymentOptionData = checkoutData?.getOfferPaymentOptions?.paymentOptions?.find(
          (po: any) => po.paymentOption === pm.key
        );

        // Uzmi prvi payment type i prvi installment
        const firstPaymentType = paymentOptionData?.paymentTypes?.[0];
        const firstInstallment = firstPaymentType?.installments?.[0];

        // Dodaj amount i discount u payment option
        return {
          ...pm,
          amount: firstInstallment?.amount,
          paymentTypeDiscount: firstInstallment?.paymentTypeDiscount || 0,
        };
      });
  }, [offerId, checkoutData?.getOfferPaymentOptions?.paymentOptions, checkoutData?.getOffer?.productTypeSpecific?.additional?.voucherPriceChangeModifier, checkoutData?.getOffer?.product?.brand?.code]);

  if (!offerId) return null;
  const isAnyIframeLoading = Object.values(iframeLoadingStates).some(iframeLoading => iframeLoading);
  if (checkoutDataLoading || isAnyIframeLoading) return <Loader page />;

  if (!checkoutData?.getOfferPaymentOptions?.paymentOptions) return null;

  const handleOpenPaymentMethod = (index: number) => {
    setOpenPaymentMethodIndex(openPaymentMethodIndex === index ? null : index);
  };

  const handleBackButton = () => {
    navigate(`${AppRoutesAdditional.OFFER.fullPath}/${offerId}`);
  };

  return (
    <Flex vertical gap={12}>
      <Flex vertical gap={24}>
        <Text type="xxxLarge" bold>
          {i18n.t('pages.checkoutPage.paymentMethod')}
        </Text>
        <Flex vertical gap={8}>
          {paymentOptions.map((pm, index) => (
            <Card padding={16} radius={12} key={index}>
              <Accordion
                key={index}
                title={
                  <Flex gap={16} align="center" justify="space-between">
                    <Flex gap={12} align="center">
                      {pm.icon}
                      <Text type="base">{pm.title}</Text>
                    </Flex>
                    <Flex vertical align="flex-end">
                      {pm.amount && (
                        <Text type="small" bold color={colors.text1}>
                          {pm.key === 'generalTransferWithInstallments' ? `${formatNumber(pm.amount / 12)}€/mj` : `${formatNumber(pm.amount)}€`}
                        </Text>
                      )}
                      {pm.key === 'generalTransferWithInstallments' && pm.amount && (
                        <Text type="tiny" color={colors.text4}>
                          {`${formatNumber(pm.amount)}€`}
                        </Text>
                      )}
                      {/* {!!pm.paymentTypeDiscount && pm.paymentTypeDiscount > 0 && (
                      <Text type="tiny" color={colors.text4}>
                        {`Uključeno ${pm.paymentTypeDiscount}% popusta`}
                      </Text>
                    )} */}
                    </Flex>
                  </Flex>
                }
                content={pm.content}
                open={openPaymentMethodIndex === index}
                expandable={true}
                expandIcon={false}
                lazy={pm.lazy}
                // expandIconComponent={<ArrowDownIcon color={colors.icon1} />}
                gap={20}
                onChange={() => handleOpenPaymentMethod(index)}
              />
            </Card>
          ))}
          <TextStyle type="tiny" color={colors.text4}>
            {i18n.t('pages.checkoutPage.paymentCardDescription')}
          </TextStyle>
        </Flex>
        <ButtonContainerStyle justify="center" align="center">
          <Button
            btnType="textBlack"
            iconPosition="left"
            text={i18n.t('pages.offerPage.backButton')}
            onClick={handleBackButton}
          >
            {<IconArrowLeft />}
          </Button>
        </ButtonContainerStyle>
        <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />
      </Flex>
    </Flex>
  );
};

export default CheckoutPageAdditional;
